import React, { useEffect, useMemo, useState } from 'react'

import { debounce } from 'lodash'

import { useGroups } from 'redux/hooks/Groups'
import { useTranslation } from 'language/translator'

import { Color as TColor } from 'themes'

import InputAdornment from 'components_new/atoms/InputAdornment'
import TextField from 'components_new/atoms/TextField'

import FormSection from 'components_new/molecules/FormSection'
import GroupAvatar from 'components_new/molecules/GroupAvatar'
import SubmitTextField from 'components_new/molecules/SubmitTextField'

interface NameProps {
  color: TColor
  onSubmit: (value: string) => void
  value: string
  variant: 'default' | 'dialog'
}

const Name = (props: NameProps) => {
  const { color, onSubmit, value, variant } = props

  const { t } = useTranslation()
  const { allGroups } = useGroups()

  const groupNames = useMemo(() => {
    return allGroups
      .map((group) => group.name)
      .filter((group) => group !== value)
  }, [allGroups])

  // internal
  const [internalValue, setInternalValue] = useState<string>(value)

  useEffect(() => {
    setInternalValue(value)
  }, [value])

  const emptyName = !internalValue
  const invalidName = groupNames.includes(internalValue)

  const debouncedSubmit = useMemo(
    () => debounce((val: string) => onSubmit(val), 500),
    [onSubmit]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedSubmit.cancel()
    }
  }, [debouncedSubmit])

  // shared props
  const TextFieldProps = {
    error: emptyName || invalidName,
    fullWidth: true,
    helperText: emptyName
      ? t('groups', 'empty_group_name_error')
      : invalidName
        ? t('groups', 'duplicate_group_name_error')
        : undefined,
    noGutter: true,
    placeholder: t('groups', 'name_group_input_placeholder'),
    required: true,
    InputProps: {
      startAdornment: (
        <InputAdornment position="start">
          <GroupAvatar
            color={color}
            size="medium"
            title={value ? value : 'N'}
          />
        </InputAdornment>
      )
    },
    value: value
  }

  return (
    <FormSection
      helperText={t('groups', 'name_helper_text')}
      importance="required"
      size="small"
      title={t('global', 'name')}
    >
      {variant === 'dialog' ? (
        <SubmitTextField
          {...TextFieldProps}
          disabledSubmit={emptyName || invalidName}
          onChange={(event) => {
            setInternalValue(event.target.value)
          }}
          onSubmit={(value) => onSubmit(value)}
        />
      ) : (
        <TextField
          {...TextFieldProps}
          value={internalValue}
          onChange={(event) => {
            const newVal = event.target.value

            setInternalValue(newVal)
            debouncedSubmit(newVal)
          }}
        />
      )}
    </FormSection>
  )
}

export default Name
